import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { useMFA } from '../contexts/MFAContext';
import multicloudImg from '../assets/landing-page/multicloud.png';
import docsIcon from '../assets/sidebar-icons/docs-link.svg';
import { useAuth, useSelectedWorkspace, useWorkspaces } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { TabIndex } from '../navigation';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import KeyIcon from '@mui/icons-material/VpnKey';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import cloudSvg from '../assets/icons-home/cloud.svg';
import { WorkspaceStatsService } from '../services/WorkspaceStatsService';
import CloudGatewayIntroModal from '../components/CloudGatewayIntroModal';
import Security2FARequiredModal from '../components/Security2FARequiredModal';
// CloudGatewayTutorialLauncher is now rendered globally in App.tsx
// OnboardingTutorial is now global in App.tsx

interface HomeProps {
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

// Icon components from Sidebar
const CodesandboxIcon = ({ size = 18, color = '#98A2B3' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
    <polyline points="7.5 19.79 7.5 14.6 3 12" />
    <polyline points="21 12 16.5 14.6 16.5 19.79" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" x2="12" y1="22.08" y2="12" />
  </svg>
);

const DepinHubIcon = ({ size = 18, color = '#98A2B3' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <line x1="12" y1="9" x2="5" y2="7" />
    <line x1="12" y1="9" x2="19" y2="7" />
    <line x1="12" y1="15" x2="5" y2="17" />
    <line x1="12" y1="15" x2="19" y2="17" />
  </svg>
);

const AIIcon = ({ size = 18, color = '#98A2B3' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

// Helper function to format bytes to human-readable format
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper function to format numbers with K/M suffix
const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const Home: React.FC<HomeProps> = ({ handleTabChange }) => {
  const navigate = useNavigate();
  const { globalData, authState, setSelectedWorkspace } = useAuth();
  const workspaces = useWorkspaces();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  const [bucketsCount, setBucketsCount] = useState(() => {
    // Charger depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.bucketsCount;
    }
    return 0;
  });
  const [reposCount, setReposCount] = useState(() => {
    // Charger depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.reposCount;
    }
    return 0;
  });
  const [aiConfigsCount, setAIConfigsCount] = useState(() => {
    // Charger depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.aiLlmsCount;
    }
    return 0;
  });
  const [publicNodesCount, setPublicNodesCount] = useState(0);
  const [privateNodesCount, setPrivateNodesCount] = useState(0);
  const [totalTokenConsumption, setTotalTokenConsumption] = useState(0);
  const [totalDataTransit, setTotalDataTransit] = useState(0);
  const [storageUnitsCount, setStorageUnitsCount] = useState(0);
  const [dealsCount, setDealsCount] = useState(0);
  const [storageTotalBytes, setStorageTotalBytes] = useState(() => {
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.bucketsTotalBytes;
    }
    return 0;
  });
  const [aiApiKeysCount, setAiApiKeysCount] = useState(() => {
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.aiApiKeysCount;
    }
    return 0;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasRepoWithApiKey, setHasRepoWithApiKey] = useState(() => {
    // Prefer explicit flag if ever set by other views (e.g., Sidebar/Repos)
    try {
      if (localStorage.getItem('hasRepoWithApiKey') === 'true') return true;
    } catch { void 0; }
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.hasRepoWithApiKey;
    }
    return false;
  });
  const [hasUploadedData, setHasUploadedData] = useState<boolean>(() => {
    // Prefer cache from Sidebar computation if available
    try {
      const cachedFlag = localStorage.getItem('hasUploadedData');
      if (cachedFlag != null) return cachedFlag === 'true';
    } catch { void 0; }
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached && typeof cached.hasUploadedData === 'boolean') return cached.hasUploadedData;
    }
    return false;
  });
  const [showOnboardingCard, setShowOnboardingCard] = useState(true);
  
  // Cloud Gateway Intro Modal state
  const [showGatewayIntro, setShowGatewayIntro] = useState(false);
  const [showTutorialLauncher, setShowTutorialLauncher] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [show2FARequiredModal, setShow2FARequiredModal] = useState(false);

  const firstName = globalData?.userProfile?.firstName || authState?.user?.name || 'User';

  // MFA state for 2FA quest
  const { mfaState } = useMFA();
  const is2FAEnabled = mfaState?.status?.isEnabled || false;
  
  // Check if workspaces exist
  const hasWorkspaces = workspaces && workspaces.length > 0;
  
  // Check if gateway intro was completed
  const isGatewayIntroCompleted = (() => {
    try {
      return localStorage.getItem('cloudGatewayIntroCompleted') === 'true';
    } catch {
      return false;
    }
  })();
  
  // Check if tutorial was dismissed
  const isTutorialDismissed = (() => {
    try {
      return localStorage.getItem('gatewayTutorialDismissed') === 'true';
    } catch {
      return false;
    }
  })();
  
  // Get saved tutorial step
  const getSavedTutorialStep = () => {
    try {
      const saved = localStorage.getItem('gatewayTutorialStep');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  };

  // Onboarding completion cache
  const isOnboardingCompletedInCache = (() => {
    try {
      return localStorage.getItem('onboardingCompleted') === 'true';
    } catch {
      return false;
    }
  })();

  type OnboardingStepId = 'profile' | '2fa' | 'bucket' | 'repo' | 'apikey' | 'upload';
  interface OnboardingStep { id: OnboardingStepId; completed: boolean; }

  const steps: OnboardingStep[] = [
    { id: 'profile', completed: true },
    { id: '2fa', completed: is2FAEnabled },
    { id: 'bucket', completed: bucketsCount > 0 },
    { id: 'repo', completed: reposCount > 0 },
    { id: 'apikey', completed: !!hasRepoWithApiKey },
    { id: 'upload', completed: !!hasUploadedData },
  ];
  const firstIncomplete = steps.find((s) => !s.completed);
  const allQuestsCompleted = steps.every((s) => s.completed);

  // Persist completion
  useEffect(() => {
    if (allQuestsCompleted && !isOnboardingCompletedInCache) {
      try { localStorage.setItem('onboardingCompleted', 'true'); } catch { void 0; }
    }
  }, [allQuestsCompleted, isOnboardingCompletedInCache]);

  // Persist per-mission flags and auto-hide when all done
  useEffect(() => {
    const mark = (key: string, v: boolean) => {
      if (v) {
        try {
          if (localStorage.getItem(key) !== 'true') localStorage.setItem(key, 'true');
        } catch { void 0; }
      }
    };
    mark('onboarding:profileCompleted', true);
    mark('onboarding:2faCompleted', is2FAEnabled);
    mark('onboarding:bucketCompleted', bucketsCount > 0);
    mark('onboarding:repoCompleted', reposCount > 0);
    mark('onboarding:apikeyCompleted', !!hasRepoWithApiKey);
    mark('onboarding:uploadCompleted', !!hasUploadedData);
    if (allQuestsCompleted) {
      setShowOnboardingCard(false);
      try { localStorage.setItem('onboardingCompleted', 'true'); } catch { void 0; }
    }
  }, [is2FAEnabled, bucketsCount, reposCount, hasRepoWithApiKey, hasUploadedData, allQuestsCompleted]);

  const getCtaForStep = (id: OnboardingStepId) => {
    switch (id) {
      case 'profile':
        return { label: 'Complete profile', action: () => navigate('/profile') };
      case '2fa':
        return {
          label: 'Set up 2FA',
          action: () => {
            try { localStorage.setItem('triggerProfileSecurity', '1'); } catch { void 0; }
            window.dispatchEvent(new CustomEvent('openProfileSecurity'));
          },
        };
      case 'bucket':
        return {
          label: '+ Add bucket',
          action: () => navigate('/cloud-storage/buckets/add'),
        };
      case 'repo':
        return { label: '+ Add repository', action: () => navigate('/repositories/inventory/add') };
      case 'apikey':
        return { label: 'Generate API key', action: () => navigate('/repositories/inventory') };
      case 'upload':
        return { label: 'Upload a file', action: () => navigate('/cloud-storage/buckets') };
      default:
        return { label: 'Learn more', action: () => window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer') };
    }
  };

  const dismissOnboarding = () => {
    setShowOnboardingCard(false);
    try { sessionStorage.removeItem('hideOverviewOnboardingPromo'); } catch { void 0; }
  };
  
  // Check if tutorial is completed
  const isTutorialCompleted = (() => {
    try {
      return localStorage.getItem('gatewayTutorialCompleted') === 'true';
    } catch { return false; }
  })();
  
  // Cloud Gateway Intro Modal Logic
  useEffect(() => {
    // Wait for global data to be ready before showing intro modal
    if (!globalData.isReady) return;
    
    // Show intro modal if:
    // 1. User has no workspaces
    // 2. Intro was not completed before
    if (!hasWorkspaces && !isGatewayIntroCompleted) {
      // Show gateway intro regardless of 2FA status
      setShowGatewayIntro(true);
    } else {
      // Hide intro if workspaces exist
      setShowGatewayIntro(false);
    }
    
    // Always show tutorial launcher if tutorial not completed (and intro is done)
    // Add a 1.5 second delay to prevent element visibility issues
    if (isGatewayIntroCompleted && !isTutorialCompleted && !isTutorialDismissed) {
      const tutorialTimer = setTimeout(() => {
        setShowTutorialLauncher(true);
        setTutorialStep(getSavedTutorialStep());
      }, 1500);
      return () => clearTimeout(tutorialTimer);
    }
  }, [globalData.isReady, hasWorkspaces, isGatewayIntroCompleted, is2FAEnabled, isTutorialDismissed, isTutorialCompleted]);
  
  // Handle 2FA setup from modal
  const handle2FASetup = () => {
    setShow2FARequiredModal(false);
    // Trigger profile security section with highlight2FA
    window.dispatchEvent(new CustomEvent('openProfileModal', { 
      detail: { section: 'Profile', tab: 'Security', highlight2FA: true } 
    }));
  };
  
  // Handle Gateway Intro completion
  const handleGatewayIntroComplete = () => {
    try {
      localStorage.setItem('cloudGatewayIntroCompleted', 'true');
    } catch { /* ignore */ }
    setShowGatewayIntro(false);
    // Show tutorial launcher after intro
    setShowTutorialLauncher(true);
  };
  
  // Handle workspace creation from intro modal
  const handleCreateWorkspaceFromIntro = async (name: string) => {
    try {
      const result = await withTokenRefresh(() =>
        apiClient.createWorkspace({ name: name.trim() })
      );
      if (result.success && result.workspace) {
        // Select the new workspace
        setSelectedWorkspace(result.workspace.id);
        // Dispatch event to refresh global data
        window.dispatchEvent(new CustomEvent('workspaceCreated', { detail: { workspaceId: result.workspace.id } }));
      }
      return result;
    } catch (error) {
      console.error('[Home] Error creating workspace:', error);
      return { success: false };
    }
  };
  
  // Tutorial launcher handlers
  const handleStartTutorial = () => {
    setShowTutorialLauncher(false);
    // Dispatch event to App.tsx to start tutorial (it persists across pages)
    window.dispatchEvent(new CustomEvent('startCloudGatewayTutorial'));
  };
  
  const handleDismissTutorial = () => {
    try {
      localStorage.setItem('gatewayTutorialDismissed', 'true');
    } catch { /* ignore */ }
    setShowTutorialLauncher(false);
  };
  
  const handleTutorialStepChange = (step: number) => {
    setTutorialStep(step);
    try {
      localStorage.setItem('gatewayTutorialStep', String(step));
    } catch { /* ignore */ }
  };
  
  const handleTutorialComplete = () => {
    setIsTutorialActive(false);
    setTutorialStep(4); // Mark as completed
    try {
      localStorage.setItem('gatewayTutorialCompleted', 'true');
      localStorage.setItem('gatewayTutorialStep', '4');
    } catch { /* ignore */ }
  };
  
  const handleTutorialClose = () => {
    setIsTutorialActive(false);
    // Keep the launcher visible so user can resume
    setShowTutorialLauncher(true);
  };

  // Open global tutorial (dispatches event to App.tsx)
  const handleRestartTutorial = () => {
    window.dispatchEvent(new CustomEvent('openTutorial'));
  };

  // Get current workspace name
  const getWorkspaceName = () => {
    const selectedWorkspaceId = localStorage.getItem('selectedWorkspaceId') || globalData.selectedWorkspaceId || '';
    if (selectedWorkspaceId === 'ALL') {
      return 'all workspaces';
    }
    const workspace = globalData.workspaces.find((w: any) => w.id === selectedWorkspaceId);
    return workspace?.name || 'workspace';
  };

  useEffect(() => {
    // Wait for globalData.isReady before loading data
    if (globalData?.isReady) {
      loadData();
    }
  }, [globalData?.isReady]);

  // Écouter les mises à jour du service centralisé
  useEffect(() => {
    const unsubscribe = WorkspaceStatsService.onStatsUpdate((stats) => {
      setBucketsCount(stats.bucketsCount);
      setReposCount(stats.reposCount);
      setAIConfigsCount(stats.aiLlmsCount);
      setHasRepoWithApiKey(stats.hasRepoWithApiKey);
      setHasUploadedData(stats.hasUploadedData);
      setStorageTotalBytes(stats.bucketsTotalBytes);
      setAiApiKeysCount(stats.aiApiKeysCount);
    });
    
    return () => unsubscribe();
  }, []);

  // Écouter les changements de workspace
  useEffect(() => {
    const handleWorkspaceChange = () => {
      loadData();
    };
    
    window.addEventListener('workspaceChanged', handleWorkspaceChange);
    
    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChange);
    };
  }, []);

  // Écouter les changements de localStorage pour détecter la création d'API keys
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key === 'hasRepoWithApiKey') {
        setHasRepoWithApiKey(e.newValue === 'true');
      }
      if (e.key.startsWith('workspace_stats_cache')) {
        // Recharger rapidement pour synchroniser l'UI
        loadData();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Écouter la création d'une API key (même onglet)
  useEffect(() => {
    const onApiKeyCreated = () => {
      try { localStorage.setItem('hasRepoWithApiKey', 'true'); } catch { void 0; }
      setHasRepoWithApiKey(true);
      const workspaceId = localStorage.getItem('selectedWorkspaceId') || globalData.selectedWorkspaceId || '';
      if (workspaceId) WorkspaceStatsService.invalidateCache(workspaceId);
      loadData();
    };
    window.addEventListener('repoApiKeyCreated', onApiKeyCreated as any);
    return () => window.removeEventListener('repoApiKeyCreated', onApiKeyCreated as any);
  }, [globalData.selectedWorkspaceId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // CRITICAL: Wait for globalData.isReady before loading stats
      if (!globalData?.isReady) {
        setIsLoading(false);
        return;
      }

      const token = authState?.token;
      const workspaceId = localStorage.getItem('selectedWorkspaceId') || globalData.selectedWorkspaceId || '';
      
      // CRITICAL: Guard against undefined/invalid workspaceId
      if (!token || !workspaceId || workspaceId === 'undefined' || workspaceId === '' || workspaceId === 'ALL') {
        console.warn('[Home] Invalid workspaceId or token, skipping stats load:', workspaceId);
        setIsLoading(false);
        return;
      }
      
      // Utiliser le service centralisé pour charger toutes les stats
      const stats = await WorkspaceStatsService.loadWorkspaceStats(
        workspaceId,
        false // Use cache
      );
      
      setBucketsCount(stats.bucketsCount);
      setReposCount(stats.reposCount);
      setAIConfigsCount(stats.aiLlmsCount);
      setHasRepoWithApiKey(stats.hasRepoWithApiKey);
      setHasUploadedData(stats.hasUploadedData);
      setStorageTotalBytes(stats.bucketsTotalBytes);
      setAiApiKeysCount(stats.aiApiKeysCount);

      // REMOVED: Double-call pattern that was causing duplicate API calls
      // The cache will be used if available, and stats will be fresh enough

      // Load Network data (Public/Private Nodes)
      try {
        // Public nodes
        const publicResponse = await withTokenRefresh(() => apiClient.getNodeInfo(1)) as any;
        if (publicResponse?.success === true && typeof publicResponse.total === 'number') {
          setPublicNodesCount(publicResponse.total);
        } else if (Array.isArray(publicResponse)) {
          setPublicNodesCount(publicResponse.length);
        }

        // Private nodes
        const orgId = globalData?.userProfile?.orgId;
        if (orgId) {
          const privateResponse = await withTokenRefresh(() => apiClient.getPrivateNodeInfo(orgId)) as any;
          if (privateResponse?.success === true && typeof privateResponse.total === 'number') {
            setPrivateNodesCount(privateResponse.total);
          } else if (Array.isArray(privateResponse?.data)) {
            setPrivateNodesCount(privateResponse.data.length);
          }
        }
      } catch (e) {
        console.warn('[Home] Failed to load bridge nodes:', e);
      }

      // Load AI Token Consumption (30 days) via V2 breakdown
      try {
        const breakdownResponse = await withTokenRefresh(() =>
          apiClient.getAiGatewayBreakdown('models', { scope: 'org', window: '30d' } as any)
        ) as any;
        if (breakdownResponse?.entities && Array.isArray(breakdownResponse.entities)) {
          const totalTokens = breakdownResponse.entities.reduce((acc: number, entity: any) => {
            return acc + (entity.totals?.tokensIn || 0) + (entity.totals?.tokensOut || 0);
          }, 0);
          setTotalTokenConsumption(totalTokens);
        }
      } catch (e) {
        console.warn('[Home] Failed to load AI token stats:', e);
      }

      // Load Data Transit (Upload + Download from workspace buckets) via V2 breakdown
      try {
        const bucketsResponse = await withTokenRefresh(() =>
          apiClient.getStorageBuckets()
        ) as any;

        if (bucketsResponse?.buckets && Array.isArray(bucketsResponse.buckets)) {
          const workspaceBuckets = bucketsResponse.buckets.filter((b: any) => b.workspaceId === workspaceId);
          const bucketIds = new Set(workspaceBuckets.map((b: any) => b.id).filter(Boolean));

          if (bucketIds.size > 0) {
            const storageBreakdown = await withTokenRefresh(() =>
              apiClient.getStorageGatewayBreakdown('buckets', { scope: 'org', window: '30d' } as any)
            ) as any;

            if (storageBreakdown?.entities && Array.isArray(storageBreakdown.entities)) {
              const totalTransit = storageBreakdown.entities
                .filter((entity: any) => bucketIds.has(entity.entityId))
                .reduce((acc: number, entity: any) => {
                  return acc + (entity.totals?.uplBytes || 0) + (entity.totals?.dwlBytes || 0);
                }, 0);
              setTotalDataTransit(totalTransit);
            }
          }
        }
      } catch (e) {
        console.warn('[Home] Failed to load bucket stats:', e);
      }

      // TODO: Load ISP Market data (Storage Units, Deals)
      // setStorageUnitsCount(...)
      // setDealsCount(...)

    } catch (error) {
      console.error('[Home] Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Cloud & AI Gateway
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          {(() => {
            const selectedWorkspaceId = localStorage.getItem('selectedWorkspaceId') || globalData.selectedWorkspaceId || '';
            const workspaceName = getWorkspaceName();
            return selectedWorkspaceId === 'ALL' 
              ? `Welcome back, ${firstName}. You are currently viewing all workspaces.` 
              : <>
                  Welcome back, {firstName}. You are currently viewing the{' '}
                  <span style={{ color: '#617BFF', fontWeight: 500 }}>{workspaceName}</span>{' '}
                  workspace.
                </>;
          })()}
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Home Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Home
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Quick overview of your workspace resources
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Platform Tutorial */}
            <Tooltip title="Platform Tour">
              <IconButton
                size="small"
                onClick={handleRestartTutorial}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F0F2FF', borderColor: '#617BFF', color: '#617BFF' } }}
              >
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main Cards Grid - 4 cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 pb-8">
        {/* Cloud Storage Card */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] p-6 hover:shadow-md transition-shadow cursor-pointer"
          onClick={(e) => handleTabChange(e, TabIndex.Dashboard)}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#ECFDF5' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
              </svg>
            </div>
            <div>
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#181D27',
                }}
              >
                Cloud Storage
              </Typography>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#717680',
                }}
              >
                Manage your buckets and data
              </Typography>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <StorageIcon sx={{ fontSize: 16, color: '#10B981' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>Buckets</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{bucketsCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUpIcon sx={{ fontSize: 16, color: '#10B981' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>Data Transit (30 Days)</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{formatBytes(totalDataTransit)}</span>
            </div>
          </div>

          <Button
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange(e, TabIndex.Dashboard);
            }}
            style={{
              textTransform: 'none',
              color: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            View
          </Button>
        </div>

        {/* Network Card */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] p-6 hover:shadow-md transition-shadow cursor-pointer"
          onClick={(e) => handleTabChange(e, TabIndex.BridgeNodes)}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#FFF4ED' }}
            >
              <CodesandboxIcon size={28} color="#F97316" />
            </div>
            <div>
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#181D27',
                }}
              >
                Network
              </Typography>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#717680',
                }}
              >
                Bridge nodes
              </Typography>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <CodesandboxIcon size={16} color="#F97316" />
                <span style={{ fontSize: '14px', color: '#535862' }}>Public Nodes</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{publicNodesCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <CodesandboxIcon size={16} color="#F97316" />
                <span style={{ fontSize: '14px', color: '#535862' }}>Private Nodes</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{privateNodesCount}</span>
            </div>
          </div>

          <Button
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange(e, TabIndex.BridgeNodes);
            }}
            style={{
              textTransform: 'none',
              color: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            View
          </Button>
        </div>

        {/* AI Card */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] p-6 transition-shadow hover:shadow-md cursor-pointer"
          onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.AIOverview)}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#FDF4FF' }}
            >
              <AIIcon size={28} color="#A855F7" />
            </div>
            <div>
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#181D27',
                }}
              >
                AI
              </Typography>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#717680',
                }}
              >
                LLM configurations
              </Typography>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <SmartToyIcon sx={{ fontSize: 16, color: '#A855F7' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>LLM</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{aiConfigsCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUpIcon sx={{ fontSize: 16, color: '#A855F7' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>Token Consumption (30 Days)</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{formatNumber(totalTokenConsumption)}</span>
            </div>
          </div>

          <Button
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange({} as React.SyntheticEvent, TabIndex.AIOverview);
            }}
            style={{
              textTransform: 'none',
              color: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            View
          </Button>
        </div>

        {/* Repositories Card */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] p-6 transition-shadow hover:shadow-md cursor-pointer"
          onClick={() => navigate('/repositories/overview')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#EEF2FF' }}
            >
              <FolderIcon sx={{ fontSize: 28, color: '#617BFF' }} />
            </div>
            <div>
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#181D27',
                }}
              >
                Repositories
              </Typography>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#717680',
                }}
              >
                Manage your repositories
              </Typography>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <KeyIcon sx={{ fontSize: 16, color: '#617BFF' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>API Keys</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{aiApiKeysCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-lg">
              <div className="flex items-center gap-2">
                <FolderIcon sx={{ fontSize: 16, color: '#617BFF' }} />
                <span style={{ fontSize: '14px', color: '#535862' }}>Resources</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#181D27' }}>{reposCount}</span>
            </div>
          </div>

          <Button
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/repositories/overview');
            }}
            style={{
              textTransform: 'none',
              color: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            View
          </Button>
        </div>
      </div>

      </div>

      {/* Cloud Gateway Intro Modal */}
      <CloudGatewayIntroModal
        open={showGatewayIntro}
        onComplete={handleGatewayIntroComplete}
        onCreateWorkspace={handleCreateWorkspaceFromIntro}
        userName={firstName}
        onBackToMain={() => {
          setShowGatewayIntro(false);
        }}
      />
      
      {/* Tutorial Launcher is now rendered globally in App.tsx */}
      
      {/* 2FA Required Modal */}
      <Security2FARequiredModal
        open={show2FARequiredModal}
        onClose={() => {
          setShow2FARequiredModal(false);
          navigate('/hub');
        }}
        onSetup2FA={handle2FASetup}
        feature="Cloud Gateway"
      />

    </div>
  );
};

export default Home;
